/**
 * External Repair (RMA) list + detail page.
 * Feb 2026 — physical send-off workflow for assets going to a vendor for repair.
 *
 * Backend endpoints:
 *   POST   /external-repairs                       — create a draft ERO
 *   POST   /external-repairs/{id}/gate-out         — dispatch to vendor
 *   POST   /external-repairs/{id}/estimate         — record vendor's quoted price
 *   POST   /external-repairs/{id}/approve-estimate — approve/reject the estimate
 *   POST   /external-repairs/{id}/gate-in          — receive back on premises
 *   POST   /external-repairs/{id}/close            — mark closed after paperwork done
 *   GET    /external-repairs/{id}/gate-out-pdf     — printable outward gate-pass
 *   GET    /external-repairs/{id}/gate-in-pdf      — printable inward gate-pass
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { hasCapability, formatDate, formatDateTime, formatINR } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Truck, Package, FileCheck2, CheckCircle2, XCircle, Loader2, Plus, ArrowLeft, Printer, Search, X } from "lucide-react";

const STATUS_META = {
  draft:             { label: "Draft",              color: "bg-slate-100 text-slate-700 border-slate-200" },
  dispatched:        { label: "At Vendor",          color: "bg-amber-100 text-amber-800 border-amber-200" },
  estimate_received: { label: "Estimate Received",  color: "bg-blue-100 text-blue-800 border-blue-200" },
  approved:          { label: "Repair Approved",    color: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  rejected:          { label: "Estimate Rejected",  color: "bg-rose-100 text-rose-800 border-rose-200" },
  repaired:          { label: "Repaired",           color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  returned:          { label: "Returned",           color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  closed:            { label: "Closed",             color: "bg-slate-200 text-slate-700 border-slate-300" },
  cancelled:         { label: "Cancelled",          color: "bg-slate-100 text-slate-500 border-slate-200" },
};

export default function ExternalRepairsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const canCreate = hasCapability(user, "create_external_repair");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/external-repairs");
      setRows(data || []);
    } catch {
      toast.error("Failed to load repair orders");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => statusFilter === "all" ? rows : rows.filter(r => r.status === statusFilter),
    [rows, statusFilter]
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-3">
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">External Repairs</h1>
          <p className="text-sm text-slate-500 mt-1">
            Track assets sent off-site to vendors for repair. Every stage is stamped and printable.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/maintenance")} data-testid="ero-back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Maintenance
          </Button>
          {canCreate && (
            <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setCreateOpen(true)} data-testid="ero-new-btn">
              <Plus className="w-4 h-4 mr-2" /> New Repair Order
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="border-b border-slate-100 pb-3 flex flex-row items-center justify-between">
          <CardTitle className="font-heading text-base">Repair Orders</CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-slate-500">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 w-40 text-sm" data-testid="ero-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {Object.entries(STATUS_META).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-sm text-slate-400 text-center">
              No repair orders yet. Hit <b>New Repair Order</b> above to start one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2">ERO No.</th>
                    <th className="text-left px-4 py-2">Vendor</th>
                    <th className="text-left px-4 py-2">Assets</th>
                    <th className="text-left px-4 py-2">Expected Return</th>
                    <th className="text-left px-4 py-2">Estimate</th>
                    <th className="text-left px-4 py-2">Status</th>
                    <th className="text-left px-4 py-2">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r) => {
                    const meta = STATUS_META[r.status] || {};
                    return (
                      <tr key={r.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setDetailId(r.id)} data-testid={`ero-row-${r.ero_id}`}>
                        <td className="px-4 py-3 font-mono text-[13px] text-[#7C1D3A]">{r.ero_id}</td>
                        <td className="px-4 py-3">{r.vendor_name}</td>
                        <td className="px-4 py-3 text-xs text-slate-600">{r.asset_snapshot}</td>
                        <td className="px-4 py-3 text-xs">{r.expected_return_date ? formatDate(r.expected_return_date) : "—"}</td>
                        <td className="px-4 py-3">{r.estimate_amount != null ? formatINR(r.estimate_amount) : <span className="text-slate-400">—</span>}</td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className={`${meta.color} border`}>{meta.label || r.status}</Badge>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">{formatDate(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {createOpen && <CreateEroDialog onClose={() => setCreateOpen(false)} onCreated={(id) => { setCreateOpen(false); load(); setDetailId(id); }} />}
      {detailId && <EroDetailDialog eroId={detailId} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}


function CreateEroDialog({ onClose, onCreated }) {
  const [vendors, setVendors] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assetQ, setAssetQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    asset_ids: [], vendor_id: "",
    problem_description: "",
    expected_return_date: "",
    vehicle_or_courier: "", docket_no: "",
    notes: "",
  });

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data || [])).catch(() => {});
    api.get("/assets?limit=2000").then((r) => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || r.data.assets || []);
      setAssets(list);
    }).catch(() => {});
  }, []);

  const assetById = useMemo(() => {
    const m = new Map();
    assets.forEach(a => m.set(a.id, a));
    return m;
  }, [assets]);

  const selectedAssets = useMemo(
    () => form.asset_ids.map(id => assetById.get(id)).filter(Boolean),
    [form.asset_ids, assetById]
  );

  const filteredAssets = useMemo(() => {
    const q = assetQ.trim().toLowerCase();
    if (!q) return assets.slice(0, 40); // show first 40 by default
    const hit = (v) => (v || "").toString().toLowerCase().includes(q);
    return assets.filter(a =>
      hit(a.name) || hit(a.asset_id) || hit(a.serial_number) ||
      hit(a.brand) || hit(a.model) || hit(a.category) || hit(a.location)
    ).slice(0, 50);
  }, [assets, assetQ]);

  const toggleAsset = (id) => setForm(f => ({
    ...f,
    asset_ids: f.asset_ids.includes(id) ? f.asset_ids.filter(x => x !== id) : [...f.asset_ids, id],
  }));

  const submit = async () => {
    if (!form.vendor_id) { toast.error("Pick a vendor"); return; }
    if (form.asset_ids.length === 0) { toast.error("Pick at least one asset"); return; }
    if (!form.problem_description.trim()) { toast.error("Describe the problem"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/external-repairs", form);
      toast.success(`Repair Order ${data.ero_id} created`);
      onCreated(data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-heading">New External Repair Order</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div>
            <Label>Vendor *</Label>
            <Select value={form.vendor_id} onValueChange={(v) => setForm(f => ({ ...f, vendor_id: v }))}>
              <SelectTrigger data-testid="ero-vendor-select"><SelectValue placeholder="Pick a vendor" /></SelectTrigger>
              <SelectContent>
                {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <Label>Assets *</Label>
              <span className="text-xs text-slate-500">{form.asset_ids.length} selected</span>
            </div>

            {/* Selected chips */}
            {selectedAssets.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2 p-2 border border-[#7C1D3A]/20 bg-[#fcf0f3]/40 rounded-md" data-testid="ero-selected-chips">
                {selectedAssets.map(a => (
                  <span key={a.id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-[#7C1D3A]/30 rounded text-xs">
                    <span className="font-mono text-[#7C1D3A]">{a.asset_id}</span>
                    <span className="text-slate-600 max-w-[160px] truncate">{a.name}</span>
                    <button type="button" onClick={() => toggleAsset(a.id)} className="text-slate-400 hover:text-rose-600" aria-label={`Remove ${a.asset_id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Search bar (mirrors Asset Registry) */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, asset ID, serial, brand, model, category, or location..."
                value={assetQ}
                onChange={(e) => setAssetQ(e.target.value)}
                className="pl-9 h-10"
                data-testid="ero-asset-search"
              />
            </div>

            {/* Results */}
            <div className="mt-2 border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="ero-asset-picker">
              {filteredAssets.length === 0 ? (
                <div className="p-4 text-center text-xs text-slate-400">
                  {assetQ ? `No assets match "${assetQ}"` : "Loading assets…"}
                </div>
              ) : filteredAssets.map(a => (
                <label key={a.id} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 ${form.asset_ids.includes(a.id) ? "bg-[#fcf0f3]/60" : ""}`}>
                  <input type="checkbox" checked={form.asset_ids.includes(a.id)} onChange={() => toggleAsset(a.id)} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-[#7C1D3A]">{a.asset_id}</span>
                      {a.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{a.category}</span>}
                    </div>
                    <div className="text-slate-700 truncate">{a.name}</div>
                    {(a.brand || a.model) && (
                      <div className="text-[11px] text-slate-400 truncate">{[a.brand, a.model].filter(Boolean).join(" · ")}</div>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 text-right shrink-0 max-w-[110px] truncate">{a.location || "—"}</div>
                </label>
              ))}
              {!assetQ && assets.length > 40 && (
                <div className="p-2 text-xs text-slate-400 text-center bg-slate-50">
                  Showing 40 of {assets.length} — type above to narrow down.
                </div>
              )}
            </div>
          </div>

          <div>
            <Label>Problem Description *</Label>
            <Textarea rows={3} value={form.problem_description} onChange={(e) => setForm(f => ({ ...f, problem_description: e.target.value }))} data-testid="ero-problem" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Expected Return Date</Label>
              <Input type="date" value={form.expected_return_date} onChange={(e) => setForm(f => ({ ...f, expected_return_date: e.target.value }))} data-testid="ero-expected-return" />
            </div>
            <div>
              <Label>Vehicle / Courier</Label>
              <Input value={form.vehicle_or_courier} onChange={(e) => setForm(f => ({ ...f, vehicle_or_courier: e.target.value }))} placeholder="Blue Dart / DTDC / own vehicle" data-testid="ero-vehicle" />
            </div>
            <div>
              <Label>Docket / AWB No.</Label>
              <Input value={form.docket_no} onChange={(e) => setForm(f => ({ ...f, docket_no: e.target.value }))} data-testid="ero-docket" />
            </div>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} data-testid="ero-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={submit} disabled={busy} data-testid="ero-submit-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Create as Draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EroDetailDialog({ eroId, onClose, onChanged }) {
  const { user } = useAuth();
  const [ero, setEro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState(null); // "estimate" | "gate_in" | "approve"
  const [draft, setDraft] = useState({});

  const canGateOut = hasCapability(user, "gate_out_external_repair");
  const canEstimate = hasCapability(user, "create_external_repair");
  const canApprove = hasCapability(user, "approve_external_repair_estimate");
  const canGateIn = hasCapability(user, "gate_in_external_repair");
  const canClose = hasCapability(user, "close_external_repair");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/external-repairs/${eroId}`);
      setEro(data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [eroId]);

  const act = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/external-repairs/${eroId}/${path}`, body || {});
      toast.success("Updated");
      setStage(null);
      setDraft({});
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const printPdf = async (kind) => {
    const url = `${api.defaults.baseURL}/external-repairs/${eroId}/${kind === "gate_out" ? "gate-out-pdf" : "gate-in-pdf"}`;
    const w = window.open(url, "_blank");
    if (!w) toast.error("Please allow pop-ups to view the PDF");
  };

  if (!ero && loading) return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent><Loader2 className="w-6 h-6 animate-spin mx-auto" /></DialogContent>
    </Dialog>
  );
  if (!ero) return null;

  const meta = STATUS_META[ero.status] || {};
  const stages = ["draft", "dispatched", "estimate_received", "approved", "returned", "closed"];
  const currentIdx = stages.indexOf(ero.status);

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            <span>{ero.ero_id}</span>
            <Badge variant="outline" className={`${meta.color} border`}>{meta.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        {/* Stage tracker */}
        <div className="flex items-center gap-2 py-2 overflow-x-auto">
          {stages.map((s, i) => (
            <div key={s} className="flex items-center gap-2 shrink-0">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${i <= currentIdx ? "bg-[#7C1D3A] text-white" : "bg-slate-200 text-slate-500"}`}>{i + 1}</div>
              <div className={`text-xs ${i <= currentIdx ? "text-slate-900" : "text-slate-400"}`}>{STATUS_META[s]?.label || s}</div>
              {i < stages.length - 1 && <div className={`w-6 h-0.5 ${i < currentIdx ? "bg-[#7C1D3A]" : "bg-slate-200"}`} />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 text-sm">
          <Kv label="Vendor" value={ero.vendor_name} />
          <Kv label="Vendor Contact" value={`${ero.vendor_contact_person || ""} · ${ero.vendor_contact_phone || ""}`.trim(" ·") || "—"} />
          <Kv label="Expected Return" value={ero.expected_return_date ? formatDate(ero.expected_return_date) : "—"} />
          <Kv label="Docket / Courier" value={`${ero.docket_no || "—"} · ${ero.vehicle_or_courier || ""}`} />
          <Kv label="Warranty Covered?" value={ero.is_warranty_covered ? "Yes" : "No"} />
          <Kv label="Chargeable?" value={ero.is_chargeable ? "Yes" : "No"} />
          <Kv label="Estimate" value={ero.estimate_amount != null ? formatINR(ero.estimate_amount) : "—"} />
          <Kv label="Actual Invoice" value={ero.actual_amount != null ? `${formatINR(ero.actual_amount)} · ${ero.invoice_no || "—"}` : "—"} />
        </div>

        <div className="mt-3">
          <div className="text-[11px] uppercase text-slate-500 tracking-wider">Problem</div>
          <div className="text-sm text-slate-800 mt-0.5">{ero.problem_description}</div>
        </div>

        {/* Assets */}
        <div className="mt-4">
          <div className="text-[11px] uppercase text-slate-500 tracking-wider">Assets ({ero.assets?.length || 0})</div>
          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 mt-1">
            {(ero.assets || []).map(a => (
              <div key={a.id} className="px-3 py-2 flex items-center justify-between text-sm">
                <div>
                  <span className="font-mono text-[#7C1D3A]">{a.asset_id}</span> · {a.name}
                </div>
                <div className="text-xs text-slate-500">{a.location}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Inline sub-forms */}
        {stage === "estimate" && (
          <div className="mt-4 p-3 bg-[#fcf0f3]/60 rounded-md space-y-2">
            <div className="text-sm font-medium">Record Vendor Estimate</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (INR) *</Label>
                <Input type="number" min="0" value={draft.estimate_amount || ""} onChange={(e) => setDraft(d => ({ ...d, estimate_amount: e.target.value }))} data-testid="ero-est-amount" />
              </div>
              <div>
                <Label>Notes</Label>
                <Input value={draft.estimate_notes || ""} onChange={(e) => setDraft(d => ({ ...d, estimate_notes: e.target.value }))} data-testid="ero-est-notes" />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setStage(null)}>Cancel</Button>
              <Button size="sm" className="bg-[#7C1D3A] hover:bg-[#6c1c36]" disabled={busy} onClick={() => act("estimate", { estimate_amount: Number(draft.estimate_amount || 0), estimate_notes: draft.estimate_notes })} data-testid="ero-est-save">
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Save Estimate
              </Button>
            </div>
          </div>
        )}
        {stage === "gate_in" && (
          <div className="mt-4 p-3 bg-[#fcf0f3]/60 rounded-md space-y-2">
            <div className="text-sm font-medium">Gate-In (asset returned)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Return Condition *</Label>
                <Select value={draft.return_condition || "working"} onValueChange={(v) => setDraft(d => ({ ...d, return_condition: v }))}>
                  <SelectTrigger data-testid="ero-return-cond"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="working">Working</SelectItem>
                    <SelectItem value="partial">Partially Repaired</SelectItem>
                    <SelectItem value="not_repaired">Not Repaired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Invoice No.</Label>
                <Input value={draft.invoice_no || ""} onChange={(e) => setDraft(d => ({ ...d, invoice_no: e.target.value }))} />
              </div>
              <div>
                <Label>Actual Amount (INR)</Label>
                <Input type="number" min="0" value={draft.actual_amount || ""} onChange={(e) => setDraft(d => ({ ...d, actual_amount: e.target.value }))} />
              </div>
              <div>
                <Label>Return Notes</Label>
                <Input value={draft.return_notes || ""} onChange={(e) => setDraft(d => ({ ...d, return_notes: e.target.value }))} />
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" size="sm" onClick={() => setStage(null)}>Cancel</Button>
              <Button size="sm" className="bg-[#7C1D3A] hover:bg-[#6c1c36]" disabled={busy} onClick={() => act("gate-in", {
                return_condition: draft.return_condition || "working",
                actual_amount: draft.actual_amount ? Number(draft.actual_amount) : null,
                invoice_no: draft.invoice_no || null,
                return_notes: draft.return_notes || null,
              })} data-testid="ero-gate-in-save">
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null} Gate-In
              </Button>
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-wrap gap-2 justify-end">
          {ero.status !== "cancelled" && (
            <Button variant="outline" onClick={() => printPdf(ero.status === "returned" || ero.status === "closed" ? "gate_in" : "gate_out")} data-testid="ero-print-btn">
              <Printer className="w-4 h-4 mr-2" /> Print {ero.status === "returned" || ero.status === "closed" ? "Gate-In" : "Gate-Out"} Pass
            </Button>
          )}
          {ero.status === "draft" && canGateOut && (
            <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" disabled={busy} onClick={() => act("gate-out")} data-testid="ero-gate-out-btn">
              <Truck className="w-4 h-4 mr-2" /> Gate-Out
            </Button>
          )}
          {(ero.status === "dispatched" || ero.status === "rejected") && canEstimate && (
            <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setStage("estimate")} data-testid="ero-record-est-btn">
              <FileCheck2 className="w-4 h-4 mr-2" /> Record Estimate
            </Button>
          )}
          {ero.status === "estimate_received" && canApprove && (
            <>
              <Button variant="outline" disabled={busy} onClick={() => act("approve-estimate", { approve: false, reason: "Too expensive" })} data-testid="ero-reject-est-btn">
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("approve-estimate", { approve: true })} data-testid="ero-approve-est-btn">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Approve
              </Button>
            </>
          )}
          {["dispatched", "approved", "estimate_received", "rejected", "repaired"].includes(ero.status) && canGateIn && (
            <Button className="bg-[#7C1D3A] hover:bg-[#6c1c36]" onClick={() => setStage("gate_in")} data-testid="ero-gate-in-btn">
              <Package className="w-4 h-4 mr-2" /> Gate-In (Received)
            </Button>
          )}
          {ero.status === "returned" && canClose && (
            <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => act("close")} data-testid="ero-close-btn">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Close
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Kv = ({ label, value }) => (
  <div>
    <div className="text-[11px] uppercase text-slate-500 tracking-wider">{label}</div>
    <div className="text-slate-800 font-medium">{value ?? "—"}</div>
  </div>
);
